import React, { memo } from 'react';
import { formatCurrency, formatPercent } from '@utils/formatters.util';
import cx from '@utils/cx.util';
import useVariation from '@hooks/useVariation';

const MobileHeaderPricing = ({ rates, item, isCustomShop, minQuantity, customRate, customizationFees }) => {
  const hasSalePrice = Boolean(rates.salePrice);
  const isGenericProduct = Boolean(item.isGenericProduct);

  const isVariant = useVariation('Enhanced PDP');

  if (isVariant)
    return (
      <div className='pricing checkout-section tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-2'>
        <div>
          <CompetitionPrice savePercent={rates.youSavePercent} />
        </div>
        <div>
          <OurPriceVariant
            rates={rates}
            item={item}
            hasSalePrice={hasSalePrice}
            isCustomShop={isCustomShop}
            minQuantity={minQuantity}
            customRate={customRate}
            customizationFees={customizationFees}
          />
        </div>
      </div>
    );

  return (
    <div className='pricing checkout-section'>
      <div className='tw-flex tw-flex-col tw-gap-1'>
        <OurPrice
          rates={rates}
          item={item}
          hasSalePrice={hasSalePrice}
          isCustomShop={isCustomShop}
          minQuantity={minQuantity}
          customRate={customRate}
          customizationFees={customizationFees}
        />
        {hasSalePrice && !isGenericProduct && <Price label='Our Price' rate={rates.ourPrice} />}
        <div>
          <CompetitionPrice savePercent={rates.youSavePercent} />
        </div>
      </div>
    </div>
  );
};

export default memo(MobileHeaderPricing);

export const Price = ({ label, rate, className = '' }) => {
  if (!rate) return null;

  return (
    <div className={cx('competitor-price', className)}>
      <p className='tw-mb-0 tw-text-[13px] tw-font-normal tw-leading-tight !tw-text-gray-dark'>
        {label} <b className='tw-ml-1 tw-font-bold tw-line-through'>{formatCurrency(rate)}</b>
      </p>
    </div>
  );
};

export const CompetitionPrice = ({ savePercent, className = '' }) => {
  const isEnhancedPDPVariant = useVariation('Enhanced PDP');

  if (savePercent <= 0) return null;

  return (
    <div className={cx('competitor-price', { className })}>
      <div
        className={cx('tw-text-xs tw-leading-tight', {
          'tw-text-[#20A458]': isEnhancedPDPVariant,
          'tw-text-onsale': !isEnhancedPDPVariant,
        })}>
        <span className='tw-mr-0 tw-font-bold'>{formatPercent(savePercent)} Off</span>{' '}
        <span className='tw-text-gray-dark'>vs The Competition</span>
      </div>
    </div>
  );
};

export const OurPrice = ({
  rates,
  item,
  hasSalePrice,
  isCustomShop,
  minQuantity,
  customRate,
  customizationFees,
}) => {
  const isEnhancedPDPVariant = useVariation('Enhanced PDP');

  return (
    <div className='price col-12'>
      <meta itemProp='priceCurrency' content='USD' />
      <meta itemProp='price' content={rates.rate} />
      {item.showRateRange ? (
        <span
          className={cx('product-price sale-rate sale-rate-label !tw-text-lg !tw-leading-5', {
            'tw-text-core-flame': isEnhancedPDPVariant,
            'on-sale': hasSalePrice && !isEnhancedPDPVariant,
          })}>
          {hasSalePrice && <span className='prefix align-middle'>NOW </span>}
          {formatCurrency(item.rate)} - {formatCurrency(item.maxRate)}
        </span>
      ) : (
        <React.Fragment>
          <span
            className={cx('product-price sale-rate sale-rate-label !tw-text-lg !tw-leading-5', {
              'tw-text-core-flame': isEnhancedPDPVariant,
              'on-sale': hasSalePrice && !isEnhancedPDPVariant,
            })}
            data-value={rates.rate}>
            {hasSalePrice && <span className='prefix align-middle'>NOW </span>}
            {formatCurrency(rates.rate)}
          </span>
          {isCustomShop && minQuantity > 1 ? (
            <div className='bulk-unit-rate'>
              Per Unit
              <br />
              (Min. Order Qty: {minQuantity})
            </div>
          ) : (
            item.rateEach != item.rate && (
              <div className='bulk-unit-rate tw-text-[11px] tw-font-medium'>
                Per Case (
                {formatCurrency(customRate?.rateEach ?? item.rateEach + (customizationFees?.perItem ?? 0))}{' '}
                each)
              </div>
            )
          )}
        </React.Fragment>
      )}
    </div>
  );
};

export const OurPriceVariant = ({
  rates,
  item,
  hasSalePrice,
  isCustomShop,
  minQuantity,
  customRate,
  customizationFees,
}) => {
  const isEnhancedPDPVariant = useVariation('Enhanced PDP');

  return (
    <div className='price col-12'>
      <meta itemProp='priceCurrency' content='USD' />
      <meta itemProp='price' content={rates.rate} />
      {item.showRateRange ? (
        <span
          className={cx('product-price sale-rate sale-rate-label !tw-text-lg !tw-leading-5', {
            'tw-text-core-flame': isEnhancedPDPVariant,
            'on-sale': hasSalePrice && !isEnhancedPDPVariant,
          })}>
          {hasSalePrice && <span className='prefix align-middle'>NOW </span>}
          {formatCurrency(item.rate)} - {formatCurrency(item.maxRate)}
        </span>
      ) : (
        <React.Fragment>
          <span className='tw-inline-flex tw-items-center tw-gap-1'>
            {hasSalePrice && (
              <span
                className='tw-text-[13px] tw-font-normal tw-text-core-deep-navy tw-line-through'
                data-value={rates.rate}>
                {formatCurrency(rates.ourPrice)}
              </span>
            )}
            <span
              className={cx('product-price sale-rate sale-rate-label !tw-text-lg !tw-leading-5', {
                'tw-text-core-flame': isEnhancedPDPVariant,
                'on-sale': hasSalePrice && !isEnhancedPDPVariant,
              })}
              data-value={rates.rate}>
              {formatCurrency(rates.rate)}
            </span>
          </span>
          {isCustomShop && minQuantity > 1 ? (
            <div className='bulk-unit-rate'>
              Per Unit
              <br />
              (Min. Order Qty: {minQuantity})
            </div>
          ) : (
            item.rateEach != item.rate && (
              <div className='bulk-unit-rate tw-text-[11px] tw-font-medium'>
                Per Case (
                {formatCurrency(customRate?.rateEach ?? item.rateEach + (customizationFees?.perItem ?? 0))}{' '}
                each)
              </div>
            )
          )}
        </React.Fragment>
      )}
    </div>
  );
};
